import React from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Stickyfill from 'stickyfill';
import { connect } from 'react-redux';
import get from 'lodash.get';

import {
  loadFront as loadFrontAction,
  loadFrontDraft as loadFrontDraftAction,
  setDraftId,
} from 'redux/modules/front';
import { setTheme as setThemeAction } from 'redux/modules/navbar';
import { globalLoadComplete } from 'redux/modules/router';
import {
  disableLazyLoadAction,
  setPageView,
  setSection,
  setVertical,
} from 'redux/modules/shared';

import Ad from 'components/Ad';
import AdsBundle from 'components/AdsBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import getLayout from 'components/layouts/getLayout';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import { FrontMetadata, GlobalMetadata } from 'components/PageMetadata';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';

import BTE from 'lib/BTE';
import Breakpoints from 'lib/Breakpoints';
import {
  curation as curationPropType,
} from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import InactivityMonitor from 'lib/InactivityMonitor';
import listenForCurationMessages from 'lib/listenForCurationMessages';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import {
  packageHasTitleOrType,
  getFirstLayout,
  getFirstPackageType,
  hasHeaderPackage,
  hasHeaderTransparentLightPackage,
  hasHeaderLightPackage,
} from 'lib/packageHelper';
import getThemesCssPath from 'lib/getThemesCssPath';

import ErrorPage from '../_error';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';

const pageView = 'front';

const mapStateToProps = ({
  front: {
    loading, curation, draftId, stackCommerceFront,
  },
  navbar: { theme },
}) => ({
  loading,
  curation,
  draftId,
  navTheme: theme,
  stackCommerceFront,
});

const mapActionsToProps = {
  loadFront: loadFrontAction,
  loadFrontDraft: loadFrontDraftAction,
  setTheme: (theme) => setThemeAction(theme),
};

function reloadWindow() {
  if (document.hasFocus()) window.location.reload(true);
}

const navbarConfig = {
  theme: ({
    content, path, section, vertical,
  }) => {
    const {
      curation: { layouts } = {},
      stackCommerceFront,
    } = content;
    const isHomePage = path === '/';
    if (hasHeaderTransparentLightPackage(layouts, vertical) && !stackCommerceFront) {
      return NAVBAR_THEME.TRANSPARENT_LIGHT;
    }
    // if section is an empty string, that means we're on the homepage
    // TODO: Telemundo - remove vertical !== noticias once Curator supports Noticias
    if (((hasHeaderLightPackage(layouts, vertical) && section.length === 0)
    || (stackCommerceFront && !isHomePage)) && vertical !== 'noticias') {
      return NAVBAR_THEME.LIGHT;
    }
    if (hasHeaderPackage(layouts) && !stackCommerceFront) {
      return NAVBAR_THEME.TRANSPARENT;
    }
    if (vertical === 'news' || vertical === 'msnbc' || vertical === 'noticias') {
      return NAVBAR_THEME.VERTICAL;
    }
    return NAVBAR_THEME.LIGHT;
  },
};

class Front extends React.Component {
  static async getInitialProps(ctx) {
    const {
      asPath,
      req: {
        query: {
          lazyload,
        },
        params: {
          draftId,
          section = '',
        },
      },
      res: {
        locals: {
          domain,
          hostname,
          services,
        },
      },
      store,
    } = ctx;
    // Can be overwritten by news/sponsoredContent
    let { vertical } = ctx.res.locals;

    const promises = [
      store.dispatch(setPageView(pageView)),
      store.dispatch(setSection(section)),
      store.dispatch(setVertical(vertical)),
    ];

    if (lazyload === 'false') {
      promises.push(store.dispatch(disableLazyLoadAction()));
    }

    await Promise.all(promises);

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const frontPromises = [];

    if (draftId) {
      store.dispatch(setDraftId(draftId)); // set for later refreshes on curation
      frontPromises.push(store.dispatch(loadFrontDraftAction(draftId)));
    } else {
      frontPromises.push(store.dispatch(loadFrontAction(vertical, section)));
    }

    await Promise.all(frontPromises);

    const { front: frontPageStateAfterLoad } = store.getState();
    const pageType = get(frontPageStateAfterLoad, 'curation.pageType', 'front');
    const statusCode = get(frontPageStateAfterLoad, 'error.status', 200);

    if (pageType === 'sponsoredContent') {
      vertical = 'sponsoredcontent';
      store.dispatch(setVertical(vertical));
      ctx.res.locals.vertical = vertical;
    }

    // override value set in _app.js
    const themesCssPath = getThemesCssPath(vertical);

    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    await store.dispatch(globalLoadComplete({
      domain,
      host: hostname,
      navigating: false,
      path: asPath,
      transporting: false,
    }));

    return {
      domain,
      draftId,
      layout,
      pageView,
      path: asPath,
      section,
      statusCode,
      themesCssPath,
      vertical,
    };
  }

  top = null;

  static propTypes = {
    curation: curationPropType.isRequired,
    domain: PropTypes.string.isRequired,
    loadFrontDraft: PropTypes.func.isRequired,
    navTheme: PropTypes.string.isRequired,
    vertical: PropTypes.string.isRequired,
    draftId: PropTypes.string,
    section: PropTypes.string,
    layout: servicesLayoutPropType.isRequired,
    themesCssPath: PropTypes.string,
    statusCode: PropTypes.number.isRequired,
    setTheme: PropTypes.func.isRequired,
  };

  static contextTypes = {
    store: PropTypes.shape({}),
  };

  static childContextTypes = {
    section: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    stickyfill: PropTypes.any,
  };

  static defaultProps = {
    draftId: null,
    section: null,
    themesCssPath: '',
  };

  getChildContext() {
    const { section } = this.props;
    return {
      section,
      stickyfill: __CLIENT__ ? Stickyfill() : null, // eslint-disable-line no-undef
    };
  }

  componentDidMount() {
    const {
      vertical,
      draftId,
      curation: { layouts, pageRoute },
      loadFrontDraft,
    } = this.props;
    const lightNavPackages = hasHeaderLightPackage(layouts, vertical);
    const isNews = vertical === 'news';
    const isMsnbc = vertical === 'msnbc';
    const isNoticias = vertical === 'noticias';
    const isHomePage = pageRoute === '/';

    // if section is an empty string, that means we're on the homepage
    if ((isNews || isMsnbc || isNoticias) && !lightNavPackages && isHomePage) {
      BTE.on('scroll', this.handleScroll, 'throttle');
    }

    if (__CLIENT__) { // eslint-disable-line no-undef
      listenForCurationMessages(vertical, () => {
        if (!draftId) return; // no draft is loaded
        loadFrontDraft(draftId);
      });

      this.monitorActivity();
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps() {
    window.parent.postMessage('refreshedFront', '*');
  }

  componentWillUnmount() {
    BTE.remove('scroll', this.handleScroll);

    InactivityMonitor.stopMonitoring();
    InactivityMonitor.removeListener(reloadWindow);
  }

  setTop() {
    const { top } = this.container.getBoundingClientRect();
    this.top = top + (!Breakpoints.isS() ? window.pageYOffset - 80 : 0);
  }

  handleScroll = (depth) => {
    this.setTop();
    const { navTheme, setTheme } = this.props;

    if (depth > this.top && navTheme !== NAVBAR_THEME.LIGHT) {
      setTheme(NAVBAR_THEME.LIGHT);
    } else if (depth <= this.top && navTheme !== NAVBAR_THEME.VERTICAL) {
      setTheme(NAVBAR_THEME.VERTICAL);
    }
  }

  monitorActivity = () => {
    const { section, vertical } = this.props;
    let inactivityDuration;

    if (!section && ['news', 'msnbc', 'today', 'noticias'].includes(vertical)) {
      inactivityDuration = 4;
    }

    if (inactivityDuration) {
      InactivityMonitor.after((inactivityDuration * 60000), reloadWindow);
      InactivityMonitor.startMonitoring();

      window.addEventListener('mousemove', InactivityMonitor.resetTimer);
      window.addEventListener('click', InactivityMonitor.resetTimer);
      window.addEventListener('scroll', InactivityMonitor.resetTimer);
      window.addEventListener('keypress', InactivityMonitor.resetTimer);
    }
  }

  interstitialAd = () => {
    const { vertical } = this.props;
    if (['news', 'today', 'msnbc', 'globalcitizen'].includes(vertical)) {
      return (
        <Ad
          slot="interstitial"
          refreshInterval={0}
        />
      );
    }
    return null;
  }

  renderLayouts() {
    const { curation, vertical } = this.props;

    if (!curation.layouts) return null;

    const {
      pageRoute, adsDisabled, autofill, layouts,
    } = curation;
    const layoutRenderer = getLayout(
      {
        vertical,
        pageRoute,
        layouts,
        adsDisabled,
        curationAutofill: autofill,
      },
      this.context,
    );

    return curation.layouts.map(layoutRenderer);
  }

  render() {
    const {
      vertical,
      curation,
      section, // need to get this to be passed in from routes.
      domain,
      layout,
      themesCssPath,
      statusCode,
    } = this.props;

    if (statusCode === 404) {
      return (
        <ErrorPage statusCode={404} vertical={vertical} layout={layout} />
      );
    }

    if (statusCode !== 200) {
      return (
        <ErrorPage statusCode={500} vertical={vertical} layout={layout} />
      );
    }

    const {
      layouts,
      pageRoute,
    } = curation;
    let url = `https://www.${domain}`;
    if (['news', 'msnbc', 'today', 'sponsoredcontent'].includes(vertical) === false) {
      url += `/${vertical}`;
    }
    if (section) {
      url += `/${section}`;
    }

    const hasHeaderPkg = hasHeaderPackage(layouts);
    let headerPkgs;
    if (hasHeaderPkg) {
      headerPkgs = `leadPkg-${getFirstLayout(layouts).packages[0].type}`;
    }

    const firstContentInZoneA = get(layouts, [0, 'packages', 0], {});

    const layoutContainerClasses = classNames(
      'layout-container',
      {
        'flat-layout': curation.layouts && curation.layouts.length === 1,
        'header-package': hasHeaderTransparentLightPackage(layouts) || hasHeaderPkg,
        'first-package-has-title-or-is-pancake': pageRoute === '/' && packageHasTitleOrType(firstContentInZoneA, 'pancake') && firstContentInZoneA.type !== 'coverSpread',
        'zone-a-margin': pageRoute === '/',
      },
      `lead-type--${getFirstPackageType(layouts)}`,
      headerPkgs,
    );

    return (
      <>
        <Head>
          {themesCssPath && (
            <link href={themesCssPath} rel="stylesheet" key={themesCssPath} />
          )}
          <IconfontStyleTag />
        </Head>

        <GlobalMetadata />
        <FrontMetadata
          curation={curation}
          firstContentInZoneA={firstContentInZoneA}
          vertical={vertical}
          section={section}
          domain={domain}
        />

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          <HeaderAndFooter layout={layout}>
            <ScrollingAnalytics contentType="front" contentUrl={url}>
              <div
                className={layoutContainerClasses}
                ref={(e) => { this.container = e; }}
              >
                {this.interstitialAd()}

                {this.renderLayouts()}
              </div>
            </ScrollingAnalytics>
          </HeaderAndFooter>
        </div>

        <AdsBundle />
        <AnalyticsLaunchScripts />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps, mapActionsToProps)(
    Front,
  ),
);
